<template>
  <form class="oauth-app-form" @submit.prevent="$emit('submit')">
    <usuario-form-section
      title="Tipo"
      :description="tipoSectionDescription"
      icon="fas fa-layer-group"
    >
      <div class="oauth-app-form__tipo-grid">
        <button
          v-for="option in tipoOptions"
          :key="option.value"
          type="button"
          class="oauth-app-form__tipo-card"
          :class="{
            'oauth-app-form__tipo-card--selected': form.tipo === option.value,
            'oauth-app-form__tipo-card--disabled': readonly || !isCreate,
          }"
          :disabled="readonly || !isCreate"
          @click="selecionarTipo(option.value)"
        >
          <span class="oauth-app-form__tipo-icon">
            <i :class="option.icon" />
          </span>
          <span class="oauth-app-form__tipo-title">{{ option.title }}</span>
          <span class="oauth-app-form__tipo-subtitle">{{ option.subtitle }}</span>
          <span class="oauth-app-form__tipo-description">{{ option.description }}</span>
        </button>
      </div>
      <p v-if="isCreate && !form.tipo" class="oauth-app-form__section-note">
        Selecione um tipo para continuar o cadastro.
      </p>
      <p v-else-if="!isCreate" class="oauth-app-form__section-note">
        O tipo não pode ser alterado após a criação.
      </p>
    </usuario-form-section>

    <template v-if="showRestOfForm">
      <oauth-tutorial-banner
        v-if="isCreate && isOAuthType"
        title="Primeira integração?"
        description="Abra o tutorial, escolha o tipo do seu app e siga o passo a passo do zero."
        link-label="Abrir tutorial"
      />

      <usuario-form-section
        :title="isShortcutType ? 'Identificação do atalho' : 'Identificação'"
        :description="identificacaoDescription"
        icon="fas fa-tag"
      >
        <div class="oauth-app-form__grid material-inputs">
          <base-input
            :label="isShortcutType ? 'Nome do atalho' : 'Nome da aplicação'"
            v-model="form.nome"
            :placeholder="isShortcutType ? 'Ex.: Documentação interna' : 'Ex.: Chatbot interno'"
            :disabled="readonly"
          />

          <div v-if="!isCreate && isOAuthType" class="oauth-app-form__readonly-id">
            <span class="oauth-app-form__readonly-label">App ID (client_id OAuth)</span>
            <code>{{ form.id }}</code>
            <p class="oauth-app-form__field-hint">
              Identificador interno gerado automaticamente. Use este valor como <code>client_id</code> na integração OAuth.
            </p>
          </div>

          <base-text-area
            label="Descrição"
            v-model="form.descricao"
            rows="2"
            :placeholder="isShortcutType
              ? 'Para que serve este atalho? (opcional)'
              : 'Para que serve esta aplicação? Quem vai utilizá-la?'"
            :disabled="readonly"
          />
        </div>
      </usuario-form-section>

      <usuario-form-section
        v-if="isShortcutType"
        title="Atalho na Home"
        description="O atalho só aparece na Home para usuários com acesso liberado na aba OAuth do cadastro."
        icon="fas fa-th-large"
      >
        <div class="oauth-app-form__atalho-grid">
          <div class="oauth-app-form__atalho-icons">
            <span class="oauth-app-form__readonly-label">Ícone do atalho</span>
            <div class="oauth-app-form__icon-grid">
              <button
                v-for="option in iconOptions"
                :key="option.value"
                type="button"
                class="oauth-app-form__icon-option"
                :class="{ 'oauth-app-form__icon-option--selected': form.icone === option.value }"
                :disabled="readonly"
                :title="option.label"
                @click="selecionarIcone(option.value)"
              >
                <i :class="option.value" />
              </button>
            </div>
          </div>

          <div class="oauth-app-form__atalho-preview">
            <span class="oauth-app-form__readonly-label">Pré-visualização</span>
            <div class="oauth-app-form__preview-tile">
              <app-icon
                :icon="form.icone || 'fas fa-plug'"
                gradient="linear-gradient(135deg, #5e5ce6, #007aff)"
                size="lg"
              />
              <span>{{ form.nome || 'Nome do atalho' }}</span>
            </div>
          </div>
        </div>

        <base-input
          label="Link de destino"
          v-model="form.linkLogin"
          placeholder="https://docs.exemplo.com/ou-qualquer-url"
          :disabled="readonly"
        >
          <template slot="helpBlock">
            <p class="oauth-app-form__field-hint">
              URL aberta ao clicar no atalho na Home do Configurador.
            </p>
          </template>
        </base-input>
      </usuario-form-section>

      <template v-if="isOAuthType">
        <usuario-form-section
          title="Autores"
          description="Usuários do Configurador creditados como criadores desta aplicação."
          icon="fas fa-user-edit"
        >
          <p class="oauth-app-form__field-hint">
            Os nomes aparecem na tela de autorização OAuth como &quot;Criado por ...&quot;.
            Selecione até 10 autores.
          </p>

          <p v-if="loadingUsers" class="oauth-app-form__section-note">
            <i class="fas fa-spinner fa-spin" /> Carregando usuários...
          </p>

          <div v-else class="oauth-app-form__author-select">
            <multiselect
              v-model="selectedAutores"
              :options="availableUsers"
              :multiple="true"
              :searchable="true"
              :internal-search="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :disabled="readonly"
              :custom-label="formatAutorLabel"
              track-by="id"
              placeholder="Busque por nome ou login..."
              :show-labels="false"
              select-label=""
              selected-label=""
              deselect-label=""
            >
              <template slot="option" slot-scope="props">
                <span class="option__desc">
                  <span class="option__title">{{ props.option.descricao || props.option.usuario }}</span>
                  <small class="option__small">{{ props.option.usuario }}</small>
                </span>
              </template>
              <template slot="noResult">
                Nenhum usuário encontrado. Tente outra busca.
              </template>
              <template slot="noOptions">
                Nenhum usuário disponível.
              </template>
            </multiselect>
          </div>
        </usuario-form-section>

        <usuario-form-section
          title="Atalho na Home (opcional)"
          description="Exibe um ícone na Home para usuários com permissão neste app OAuth."
          icon="fas fa-th-large"
        >
          <label class="oauth-app-form__status">
            <input
              type="checkbox"
              :checked="form.exibirAtalho"
              :disabled="readonly"
              @change="atualizarExibirAtalho($event.target.checked)"
            />
            Exibir atalho na Home para quem tiver acesso a este app
          </label>

          <div v-if="form.exibirAtalho" class="oauth-app-form__atalho-grid">
            <div class="oauth-app-form__atalho-icons">
              <span class="oauth-app-form__readonly-label">Ícone do atalho</span>
              <div class="oauth-app-form__icon-grid">
                <button
                  v-for="option in iconOptions"
                  :key="option.value"
                  type="button"
                  class="oauth-app-form__icon-option"
                  :class="{ 'oauth-app-form__icon-option--selected': form.icone === option.value }"
                  :disabled="readonly"
                  :title="option.label"
                  @click="selecionarIcone(option.value)"
                >
                  <i :class="option.value" />
                </button>
              </div>
            </div>

            <div class="oauth-app-form__atalho-preview">
              <span class="oauth-app-form__readonly-label">Pré-visualização</span>
              <div class="oauth-app-form__preview-tile">
                <app-icon
                  :icon="form.icone || 'fas fa-plug'"
                  gradient="linear-gradient(135deg, #5e5ce6, #007aff)"
                  size="lg"
                />
                <span>{{ form.nome || 'Nome do app' }}</span>
              </div>
            </div>
          </div>

          <base-input
            v-if="form.exibirAtalho"
            label="Link de login / entrada do app"
            v-model="form.linkLogin"
            placeholder="https://chatbots.exemplo.com/login"
            :disabled="readonly"
          >
        <template slot="helpBlock">
          <p class="oauth-app-form__field-hint">
            URL aberta ao clicar no atalho (página de login ou home do app terceiro).
            Use a <strong>página inicial</strong> do app, não a URL de callback OAuth.
          </p>
        </template>
          </base-input>
        </usuario-form-section>

        <usuario-form-section
          title="URLs de redirecionamento"
          description="Para onde o usuário volta após autorizar o login nesta aplicação."
          icon="fas fa-external-link-alt"
        >
          <div class="oauth-app-form__info-box">
            <p>
              Após o login no Configurador, o navegador é redirecionado para uma destas URLs com um
              código de autorização. Cadastre <strong>uma URL por linha</strong>.
            </p>
            <p>
              Use <code>https://</code> em produção. Em desenvolvimento local,
              <code>http://localhost</code> também é aceito.
            </p>
          </div>

          <base-text-area
            label="Redirect URIs"
            v-model="form.redirectUrisText"
            :rows="4"
            :placeholder="redirectPlaceholder"
            :disabled="readonly"
          />

          <p class="oauth-app-form__field-hint">
            Exemplo para o tipo selecionado:
            <code class="oauth-app-form__example">{{ redirectExample }}</code>
          </p>
        </usuario-form-section>

        <usuario-form-section
          title="Login OAuth (scopes OIDC)"
          description="Dados de identidade que o app pode solicitar durante o login — independente dos perfis de negócio abaixo."
          icon="fas fa-shield-alt"
        >
          <div class="oauth-app-form__scopes-block">
            <p class="oauth-app-form__field-hint">
              Escopos padrão do protocolo OAuth/OpenID. Marque o que esta integração precisa ler do usuário.
            </p>
            <ul class="oauth-app-form__scope-list">
              <li
                v-for="scope in standardScopeOptions"
                :key="scope.value"
                class="oauth-app-form__scope-item"
              >
                <label class="oauth-app-form__scope-option">
                  <input
                    type="checkbox"
                    :checked="form.standardScopes.includes(scope.value)"
                    :disabled="readonly || scope.required"
                    @change="toggleStandardScope(scope.value, $event.target.checked)"
                  />
                  <span class="oauth-app-form__scope-body">
                    <code>{{ scope.label }}</code>
                    <span>{{ scope.description }}</span>
                  </span>
                </label>
              </li>
            </ul>
          </div>

          <div v-if="scopesPreview.length" class="oauth-app-form__preview">
            <span class="oauth-app-form__preview-label">Scopes OAuth gerados:</span>
            <code>{{ scopesPreview.join(', ') }}</code>
          </div>
        </usuario-form-section>

        <usuario-form-section
          title="Permissões personalizadas do app"
          description="Opcional. Use perfis se o app tiver papéis internos (ex.: comercial, admin). O código vai no token; a descrição ajuda quem configura usuários."
          icon="fas fa-user-tag"
        >
          <ul v-if="form.appRoles.length" class="oauth-app-form__role-list">
            <li
              v-for="(role, index) in form.appRoles"
              :key="`role-${index}`"
              class="oauth-app-form__role-row"
            >
              <base-input
                label="Código do perfil"
                :value="role.codigo"
                placeholder="ex: comercial"
                input-classes="text-lowercase"
                :disabled="readonly"
                @input="atualizarAppRoleCampo(index, 'codigo', $event)"
              />
              <base-input
                label="Descrição"
                :value="role.descricao"
                placeholder="Para que serve este perfil?"
                :disabled="readonly"
                @input="atualizarAppRoleCampo(index, 'descricao', $event)"
              />
              <button
                v-if="!readonly"
                type="button"
                class="oauth-app-form__role-remove"
                title="Remover perfil"
                @click="removerAppRole(index)"
              >
                <i class="fas fa-trash-alt" />
              </button>
            </li>
          </ul>

          <div v-else class="oauth-app-form__role-empty">
            <p>Nenhum perfil cadastrado ainda.</p>
            <p class="oauth-app-form__role-empty-hint">
              Opcional. Só cadastre perfis se for usar o scope permissions neste app.
            </p>
          </div>

          <base-button
            v-if="!readonly"
            type="outline-secondary"
            class="btn-sm oauth-app-form__role-add"
            @click="adicionarAppRole"
          >
            <i class="fas fa-plus" /> Adicionar perfil
          </base-button>
        </usuario-form-section>
      </template>

      <usuario-form-section
        v-if="!isCreate"
        :title="isShortcutType ? 'Status do atalho' : 'Status'"
        :description="isShortcutType
          ? 'Atalhos inativos somem da Home.'
          : 'Apps inativas não podem iniciar novos logins OAuth.'"
        icon="fas fa-toggle-on"
      >
        <label class="oauth-app-form__status">
          <input type="checkbox" v-model="form.ativo" :disabled="readonly" />
          {{ isShortcutType ? 'Atalho ativo' : 'Aplicação ativa' }}
        </label>
      </usuario-form-section>

      <div v-if="generatedSecret" class="oauth-app-form__secret">
        <h4>Integração criada com sucesso</h4>
        <p v-if="form.id" class="oauth-app-form__field-hint">
          App ID (<code>client_id</code>): <code>{{ form.id }}</code>
        </p>
        <p>
          Client Secret gerado. Copie e guarde em local seguro — este valor
          <strong>não será exibido novamente</strong>.
        </p>
        <code>{{ generatedSecret }}</code>
      </div>

      <div v-if="!readonly" class="oauth-app-form__actions">
        <base-button type="secondary" @click="$emit('cancel')" :disabled="saving">
          {{ generatedSecret ? 'Voltar para listagem' : 'Cancelar' }}
        </base-button>
        <base-button
          v-if="!generatedSecret"
          type="primary"
          native-type="submit"
          :disabled="saving || (isCreate && !form.tipo)"
        >
          <i :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'" />
          {{ saving ? 'Salvando...' : submitLabel }}
        </base-button>
      </div>
    </template>
  </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import UsuarioFormSection from '@/components/usuarios/UsuarioFormSection.vue';
import OAuthTutorialBanner from '@/components/oauth/OAuthTutorialBanner.vue';
import AppIcon from '@/components/layout/AppIcon.vue';
import {
  OAUTH_TIPO_OPTIONS,
  OAUTH_STANDARD_SCOPES,
  OAUTH_APP_ICON_OPTIONS,
  buildScopesPreview,
  createEmptyAppRole,
  normalizeAppRoleKey,
  isShortcutOnlyType,
  isOAuthIntegrationType,
} from '@/lib/oauthAppFormUtils';

export default {
  name: 'OAuthAppForm',
  components: {
    Multiselect,
    UsuarioFormSection,
    OAuthTutorialBanner,
    AppIcon,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'edit'].includes(value),
    },
    saving: {
      type: Boolean,
      default: false,
    },
    generatedSecret: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loadingUsers: false,
    };
  },
  computed: {
    ...mapGetters('usuarios', ['getCustomerList']),
    availableUsers() {
      return [...(this.getCustomerList || [])]
        .filter((user) => user.id)
        .sort((left, right) => {
          const leftLabel = String(left.descricao || left.usuario || '').toLowerCase();
          const rightLabel = String(right.descricao || right.usuario || '').toLowerCase();
          return leftLabel.localeCompare(rightLabel, 'pt-BR');
        });
    },
    form: {
      get() {
        return this.value;
      },
      set(nextValue) {
        this.$emit('input', nextValue);
      },
    },
    isCreate() {
      return this.mode === 'create';
    },
    readonly() {
      return false;
    },
    showRestOfForm() {
      return !this.isCreate || Boolean(this.form.tipo);
    },
    isShortcutType() {
      return isShortcutOnlyType(this.form.tipo);
    },
    isOAuthType() {
      return isOAuthIntegrationType(this.form.tipo);
    },
    tipoSectionDescription() {
      if (this.isCreate && !this.form.tipo) {
        return 'Comece escolhendo o que você quer cadastrar.';
      }

      if (this.isShortcutType) {
        return 'Atalho simples na Home — sem login OAuth, mas com controle de acesso por usuário.';
      }

      return 'Integração OAuth/OIDC. A opção híbrida cobre frontend (PKCE) e backend (Client Secret).';
    },
    identificacaoDescription() {
      return this.isShortcutType
        ? 'Como o atalho aparecerá na Home do Configurador.'
        : 'Como esta aplicação aparecerá no Configurador e no fluxo de autorização OAuth.';
    },
    tipoOptions() {
      return OAUTH_TIPO_OPTIONS;
    },
    standardScopeOptions() {
      return OAUTH_STANDARD_SCOPES;
    },
    iconOptions() {
      return OAUTH_APP_ICON_OPTIONS;
    },
    selectedTipo() {
      return OAUTH_TIPO_OPTIONS.find((item) => item.value === this.form.tipo) || null;
    },
    redirectPlaceholder() {
      return this.selectedTipo?.redirectExample || '';
    },
    redirectExample() {
      return (this.selectedTipo?.redirectExample || '').split('\n')[0] || '—';
    },
    scopesPreview() {
      return buildScopesPreview(
        this.form.id,
        this.form.standardScopes,
        this.form.appRoles,
      );
    },
    submitLabel() {
      if (this.isCreate && this.isShortcutType) {
        return 'Criar atalho';
      }

      return this.isCreate ? 'Criar aplicação' : 'Salvar alterações';
    },
    selectedAutores: {
      get() {
        const ids = new Set(this.form.autores || []);
        return this.availableUsers.filter((user) => ids.has(user.id));
      },
      set(users) {
        if (this.readonly) {
          return;
        }

        const nextIds = (users || [])
          .slice(0, 10)
          .map((user) => user.id)
          .filter(Boolean);

        this.$emit('input', { ...this.form, autores: nextIds });
      },
    },
  },
  async mounted() {
    if (!this.isShortcutType) {
      this.loadingUsers = true;
      await this.fetchCustomers();
      this.loadingUsers = false;
    }
  },
  methods: {
    ...mapActions('usuarios', ['fetchCustomers']),
    formatAutorLabel(user) {
      return user?.descricao || user?.usuario || '';
    },
    selecionarIcone(value) {
      if (this.readonly) {
        return;
      }
      this.$emit('input', { ...this.form, icone: value });
    },
    atualizarExibirAtalho(checked) {
      this.$emit('input', { ...this.form, exibirAtalho: checked });
    },
    selecionarTipo(value) {
      if (this.readonly || !this.isCreate) {
        return;
      }

      const next = { ...this.form, tipo: value };

      if (value === 'shortcut') {
        next.exibirAtalho = true;
        next.redirectUrisText = '';
        next.standardScopes = [];
        next.appRoles = [];
        next.autores = [];
      } else if (isShortcutOnlyType(this.form.tipo)) {
        next.exibirAtalho = false;
        next.linkLogin = '';
        next.standardScopes = ['openid', 'profile', 'permissions'];
        next.appRoles = [];
        next.autores = [];
      }

      this.$emit('input', next);

      if (value !== 'shortcut' && !this.loadingUsers && !this.availableUsers.length) {
        this.loadingUsers = true;
        this.fetchCustomers().finally(() => {
          this.loadingUsers = false;
        });
      }
    },
    toggleStandardScope(scope, checked) {
      const current = [...(this.form.standardScopes || [])];
      const next = checked
        ? [...new Set([...current, scope])]
        : current.filter((item) => item !== scope);

      if (!next.includes('openid')) {
        next.unshift('openid');
      }

      this.$emit('input', { ...this.form, standardScopes: next });
    },
    adicionarAppRole() {
      this.$emit('input', {
        ...this.form,
        appRoles: [...(this.form.appRoles || []), createEmptyAppRole()],
      });
    },
    atualizarAppRoleCampo(index, field, value) {
      const nextRoles = [...(this.form.appRoles || [])];
      const current = { ...nextRoles[index] };

      if (field === 'codigo') {
        current.codigo = normalizeAppRoleKey(value);
      } else {
        current.descricao = String(value || '');
      }

      nextRoles[index] = current;
      this.$emit('input', { ...this.form, appRoles: nextRoles });
    },
    removerAppRole(index) {
      const nextRoles = [...(this.form.appRoles || [])];
      nextRoles.splice(index, 1);
      this.$emit('input', { ...this.form, appRoles: nextRoles });
    },
  },
};
</script>

<style scoped>
.oauth-app-form__grid {
  display: grid;
  gap: var(--spacing-md);
}

.oauth-app-form__field-hint {
  margin: var(--spacing-xs) 0 0;
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.5;
}

.oauth-app-form__readonly-id {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.oauth-app-form__readonly-label {
  font-size: 0.85rem;
  color: var(--text-muted);
}

.oauth-app-form__readonly-id code {
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-hover);
  border-radius: var(--border-radius-sm);
  color: var(--text-primary);
}

.oauth-app-form__tipo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--spacing-md);
}

.oauth-app-form__tipo-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xs);
  padding: var(--spacing-md);
  border: 2px solid var(--border-primary);
  border-radius: var(--border-radius-md);
  background: var(--bg-primary);
  color: var(--text-primary);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.oauth-app-form__tipo-card:hover:not(:disabled) {
  border-color: var(--color-primary);
}

.oauth-app-form__tipo-card--selected {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 1px var(--color-primary);
}

.oauth-app-form__tipo-card--disabled {
  cursor: default;
  opacity: 0.85;
}

.oauth-app-form__tipo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-sm);
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  color: var(--text-inverse);
}

.oauth-app-form__tipo-title {
  font-weight: 600;
  font-size: 1rem;
}

.oauth-app-form__tipo-subtitle {
  font-size: 0.85rem;
  color: var(--text-muted);
}

.oauth-app-form__tipo-description {
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--text-secondary);
}

.oauth-app-form__section-note {
  margin: var(--spacing-md) 0 0;
  font-size: 0.85rem;
  color: var(--text-muted);
  font-style: italic;
}

.oauth-app-form__info-box {
  margin-bottom: var(--spacing-md);
  padding: var(--spacing-md);
  border-radius: var(--border-radius-sm);
  background: var(--bg-hover);
  border: 1px solid var(--border-primary);
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.5;
}

.oauth-app-form__info-box p {
  margin: 0 0 var(--spacing-sm);
}

.oauth-app-form__info-box p:last-child {
  margin-bottom: 0;
}

.oauth-app-form__example {
  display: inline-block;
  margin-top: var(--spacing-xs);
  word-break: break-all;
}

.oauth-app-form__role-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.oauth-app-form__role-row {
  display: grid;
  grid-template-columns: minmax(160px, 1fr) minmax(220px, 2fr) auto;
  gap: var(--spacing-md);
  align-items: end;
  padding: var(--spacing-md);
  border: 1px solid var(--border-primary);
  border-radius: var(--border-radius-md);
  background: var(--bg-primary);
}

.oauth-app-form__role-empty {
  text-align: center;
  padding: var(--spacing-lg) var(--spacing-md);
  border: 1px dashed var(--border-primary);
  border-radius: var(--border-radius-md);
  color: var(--text-muted);
}

.oauth-app-form__role-empty p {
  margin: 0;
}

.oauth-app-form__role-empty-hint {
  margin-top: var(--spacing-xs) !important;
  font-size: 0.85rem;
}

.oauth-app-form__role-remove {
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  margin-bottom: 0.35rem;
}

.oauth-app-form__role-remove:hover {
  color: var(--color-danger, #dc3545);
}

.oauth-app-form__role-add {
  margin-top: var(--spacing-md);
}

@media (max-width: 768px) {
  .oauth-app-form__role-row {
    grid-template-columns: 1fr auto;
  }

  .oauth-app-form__role-row .base-input:first-child {
    grid-column: 1 / -1;
  }

  .oauth-app-form__role-row .base-input:nth-child(2) {
    grid-column: 1;
  }

  .oauth-app-form__role-remove {
    grid-column: 2;
    align-self: end;
    margin-bottom: 0.35rem;
  }
}

.oauth-app-form__atalho-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--spacing-lg);
  margin: var(--spacing-md) 0;
}

@media (max-width: 767px) {
  .oauth-app-form__atalho-grid {
    grid-template-columns: 1fr;
  }
}

.oauth-app-form__icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(44px, 1fr));
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xs);
  max-height: 220px;
  overflow-y: auto;
  padding-right: var(--spacing-xs);
}

.oauth-app-form__icon-option {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px solid var(--border-primary);
  border-radius: var(--border-radius-sm);
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
}

.oauth-app-form__icon-option--selected {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 1px var(--color-primary);
}

.oauth-app-form__icon-option:disabled {
  cursor: default;
  opacity: 0.85;
}

.oauth-app-form__preview-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 96px;
  font-size: 0.72rem;
  color: var(--text-primary);
  text-align: center;
}

.oauth-app-form__scopes-block + .oauth-app-form__scopes-block {
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--border-primary);
}

.oauth-app-form__scopes-title {
  margin: 0 0 var(--spacing-xs);
  font-size: 0.95rem;
  color: var(--text-primary);
}

.oauth-app-form__scope-list {
  list-style: none;
  margin: var(--spacing-md) 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.oauth-app-form__scope-option {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  margin: 0;
  cursor: pointer;
}

.oauth-app-form__scope-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.oauth-app-form__scope-body code,
.oauth-app-form__scope-body strong {
  color: var(--text-primary);
}

.oauth-app-form__preview {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-md);
  border-radius: var(--border-radius-sm);
  background: var(--bg-elevated);
  border: 1px dashed var(--border-primary);
}

.oauth-app-form__preview-label {
  display: block;
  margin-bottom: var(--spacing-xs);
  font-size: 0.85rem;
  color: var(--text-muted);
}

.oauth-app-form__preview code {
  word-break: break-word;
  color: var(--text-primary);
}

.oauth-app-form__status {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0;
  color: var(--text-primary);
}

.oauth-app-form__secret {
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-lg);
  border-radius: var(--border-radius-md);
  background: var(--bg-hover);
  border: 1px solid var(--color-warning);
}

.oauth-app-form__secret h4 {
  margin: 0 0 var(--spacing-sm);
  color: var(--text-primary);
}

.oauth-app-form__secret p {
  margin: 0 0 var(--spacing-md);
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.oauth-app-form__secret code {
  display: block;
  word-break: break-all;
  color: var(--text-primary);
}

.oauth-app-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.oauth-app-form__author-select {
  max-width: 560px;
}

.oauth-app-form__author-select .multiselect {
  width: 100%;
}
</style>
